import { useState } from 'react'
import { GlobeAltIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline'
import { customerApi } from '../../../services/api'

const PROGRESS_STAGES = [
  { key: 'starting', label: 'Starting...' },
  { key: 'scraping', label: 'Scraping website content...' },
  { key: 'processing', label: 'Processing and embedding content...' },
  { key: 'ready', label: 'Knowledge base ready!' },
]

export default function Step2AddContent({ onNext, onSkipStep }) {
  const [url, setUrl] = useState('')
  const [kbName, setKbName] = useState('')
  const [scraping, setScraping] = useState(false)
  const [stage, setStage] = useState(null)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [knowledgeBaseId, setKnowledgeBaseId] = useState(null)

  const handleScrape = async () => {
    if (!url.trim()) {
      setError('Please enter a website URL')
      return
    }

    let normalizedUrl = url.trim()
    if (!/^https?:\/\//i.test(normalizedUrl)) {
      normalizedUrl = 'https://' + normalizedUrl
    }

    const name = kbName.trim() || new URL(normalizedUrl).hostname

    setError('')
    setScraping(true)
    setStage('starting')

    try {
      // Simulate progress stages with timers
      const stageTimer1 = setTimeout(() => setStage('scraping'), 2000)
      const stageTimer2 = setTimeout(() => setStage('processing'), 10000)

      const response = await customerApi.post(
        '/customer/knowledge-base/scrape-website',
        { url: normalizedUrl, name, description: 'Created from ' + normalizedUrl, use_js: null },
        { timeout: 300000 }
      )

      clearTimeout(stageTimer1)
      clearTimeout(stageTimer2)

      if (response.data.success) {
        setStage('ready')
        setKnowledgeBaseId(response.data.data?.knowledge_base_id)
        setSuccess(true)
      } else {
        throw new Error(response.data.message || 'Scraping failed')
      }
    } catch (err) {
      setStage(null)
      setError(
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.message ||
        'Failed to scrape website. Please check the URL and try again.'
      )
    } finally {
      setScraping(false)
    }
  }

  const handleContinue = () => {
    onNext({ knowledge_base_id: knowledgeBaseId, url })
  }

  return (
    <div className="py-2">
      <h2 className="text-xl font-bold text-slate-900 mb-1">Add your content</h2>
      <p className="text-slate-500 mb-6">
        Enter your website URL and we'll import your content to train your AI assistant.
      </p>

      {/* URL Input */}
      <div className="space-y-4 mb-6">
        <div>
          <label htmlFor="kb-name" className="block text-sm font-medium text-slate-700 mb-1">
            Knowledge base name
          </label>
          <input
            id="kb-name"
            type="text"
            value={kbName}
            onChange={(e) => setKbName(e.target.value)}
            placeholder="My Website Knowledge Base"
            disabled={scraping || success}
            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-slate-50 disabled:text-slate-400"
          />
        </div>

        <div>
          <label htmlFor="website-url" className="block text-sm font-medium text-slate-700 mb-1">
            Website URL
          </label>
          <div className="relative">
            <GlobeAltIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              id="website-url"
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://yourwebsite.com"
              disabled={scraping || success}
              className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-slate-50 disabled:text-slate-400"
            />
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="flex items-start gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-xl">
          <ExclamationCircleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Progress stages */}
      {stage && (
        <div className="mb-6 p-4 bg-slate-50 rounded-xl">
          <div className="space-y-2">
            {PROGRESS_STAGES.map(({ key, label }) => {
              const stageIndex = PROGRESS_STAGES.findIndex(s => s.key === key)
              const currentIndex = PROGRESS_STAGES.findIndex(s => s.key === stage)
              const isActive = stageIndex === currentIndex
              const isComplete = stageIndex < currentIndex
              const isPending = stageIndex > currentIndex

              return (
                <div key={key} className="flex items-center gap-3">
                  {isComplete && <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0" />}
                  {isActive && (
                    <div className="h-5 w-5 flex-shrink-0 flex items-center justify-center">
                      <div className="h-3 w-3 rounded-full bg-indigo-600 animate-pulse" />
                    </div>
                  )}
                  {isPending && (
                    <div className="h-5 w-5 flex-shrink-0 flex items-center justify-center">
                      <div className="h-3 w-3 rounded-full bg-slate-300" />
                    </div>
                  )}
                  <span
                    className={`text-sm ${
                      isComplete ? 'text-green-700' :
                      isActive ? 'text-indigo-700 font-medium' :
                      'text-slate-400'
                    }`}
                  >
                    {label}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Action buttons */}
      {success ? (
        <button
          onClick={handleContinue}
          className="w-full px-8 py-3 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 transition-colors"
        >
          Content added! Continue
        </button>
      ) : (
        <button
          onClick={handleScrape}
          disabled={scraping}
          className="w-full px-8 py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {scraping ? (
            <>
              <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Scanning...
            </>
          ) : (
            'Scan Website'
          )}
        </button>
      )}

      {/* Skip option */}
      {!success && !scraping && (
        <div className="mt-6 text-center">
          <button
            onClick={onSkipStep}
            className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
          >
            Skip for now
          </button>
          <p className="text-xs text-slate-400 mt-1">
            You can add content later, but your assistant won't have any knowledge yet
          </p>
        </div>
      )}
    </div>
  )
}
